/**
 * Decléor — close the mobile menu drawer when the viewport stops being a mobile one.
 *
 * Resizing a window with the drawer open left it on screen next to a full desktop header: the drawer
 * has no close-on-resize of its own, and the page behind it stays scroll-locked (`data-scroll-locked`
 * on <html>), so the visitor was stuck behind a panel that no longer belonged to the layout.
 *
 * WHY THIS DOES NOT TEST A PIXEL WIDTH
 * ---------------------------------------------------------------------------
 * "Mobile" is not a width in this theme. theme.css's "Large / Small Display Logic" swaps the headers
 * at 480px, and then two further rules put the MOBILE header back at any width at all:
 *
 *     @media (pointer: coarse) { .header__desktop { display: none } .header__mobile { display: grid } }
 *     .js__show__mobile .header__desktop { display: none }
 *
 * The first is a touch laptop or an iPad — 1024px wide and legitimately on the drawer. The second is
 * theme.js's own fallback when the desktop nav does not fit the available width, which depends on the
 * menu's length, the logo's width and the merchant's nav font size. Any `matchMedia('(min-width: N)')`
 * would therefore close the drawer out from under an iPad user, or leave it open on a narrow desktop
 * that is genuinely in drawer mode.
 *
 * So the condition is the HAMBURGER ITSELF. If the button that opens the drawer is not rendered, the
 * drawer is not part of this layout and should not be on screen. That is the theme's own decision,
 * whatever combination of width, pointer type and overflow produced it, and it stays correct if the
 * merchant switches header style or the breakpoints change in a theme update.
 *
 * Closing goes through the theme's own toggle rather than hiding anything: a `display: none` would
 * leave `data-scroll-locked` set and the page unscrollable. Read in theme.js's Drawer class, the
 * toggle branches on `drawer--visible` (`theme:drawer:toggle` → `theme:drawer:close`), so a click on
 * that button can only ever CLOSE an open drawer — it cannot reopen it — and `hideDrawer()` runs the
 * full path: `theme:scroll:unlock`, `theme:sliderule:close` to reset the panes, `removeTrapFocus()`.
 *
 * Listens on `theme:resize`, which theme.js dispatches on `document` with `bubbles: true` (verified,
 * assets/theme.js line ~348), plus a native `resize` fallback. theme.js is loaded before this file and
 * both are deferred, so its own listeners are registered — and therefore run — first; the layout has
 * already settled by the time check() reads it. Orientation changes on a phone are safe by
 * construction: the hamburger stays visible, so nothing fires.
 */
(function () {
  var TOGGLE = '[data-drawer-toggle="hamburger"]';
  var frame = null;

  /* The drawer's own close button carries the same data-drawer-toggle as the hamburger, so the opener
     has to be told apart from it — it is the one outside the drawer, in the mobile header row. */
  function opener() {
    var nodes = document.querySelectorAll(TOGGLE);
    var i;
    for (i = 0; i < nodes.length; i += 1) {
      if (!nodes[i].closest('.drawer')) {
        return nodes[i];
      }
    }
    return null;
  }

  function drawer() {
    return document.querySelector('.header__drawer[data-drawer="hamburger"]');
  }

  /* getClientRects() is empty for `display: none` on the element or on any ancestor, which is exactly
     the set of rules that hide the mobile header — and unlike offsetParent it is not fooled by a
     `position: fixed` ancestor. visibility is checked separately because it does not collapse boxes. */
  function isVisible(el) {
    if (!el || !el.getClientRects().length) {
      return false;
    }
    return getComputedStyle(el).visibility !== 'hidden';
  }

  function check() {
    var node = drawer();
    if (!(node && node.classList.contains('drawer--visible'))) {
      return;
    }
    if (isVisible(opener())) {
      return;
    }

    var close = node.querySelector('.drawer__close' + TOGGLE) || node.querySelector(TOGGLE);
    if (!close) {
      return;
    }
    close.click();

    /* theme.js's hideDrawer() ends on `this.buttons[0].focus()`, and buttons[0] is the hamburger —
       which is precisely the element that just stopped being rendered, so that call is a silent no-op
       and focus stays on the close button inside a drawer that is now `visibility: hidden`. The
       browser only blurs it when `display-none` lands 800ms later. Blurring it here closes that
       window, so focus never sits in a hidden subtree. */
    if (document.activeElement && node.contains(document.activeElement)) {
      document.activeElement.blur();
    }
  }

  function onNativeResize() {
    if (frame) {
      return;
    }
    frame = requestAnimationFrame(function () {
      frame = null;
      check();
    });
  }

  document.addEventListener('theme:resize', check);

  /* Fallback, debounced on a frame so it cannot fight the theme's own resize work. Harmless when
     theme:resize also fires: check() is idempotent and returns early unless the drawer is open. */
  window.addEventListener('resize', onNativeResize, { passive: true });
})();
